import { SamplesBrand } from 'constants/brands';
import React from 'react';
import {
    Text,
    Group,
    DropdownMenu,
    DownloadOutlined,
    Button,
} from '@landr/maestro';
import { t, Trans } from '@lingui/macro';
import {
    CreatorDownloadSource,
    CreatorCollectionPlaybackMeta,
    Sample,
} from 'types';
import RemoveFromCreatorButton from 'components/Creator/RemoveFromCreatorButton';
import AddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { ShareCreator } from '../../ShareCreator';
import { getCollectionPlaceholder } from '../CreatorHeader.helpers';
import { useClickHandlers } from './CreatorHeaderActionsGroup.hooks';
import { Styled } from './CreatorHeaderActionsGroup.styled';

export interface CreatorHeaderActionsGroupProps {
    creatorDownloadSource: CreatorDownloadSource;
    samples: Sample[];
    playbackMeta?: CreatorCollectionPlaybackMeta;
    isDisabled: boolean;
}

export const CreatorHeaderActionsGroup: React.FC<
    CreatorHeaderActionsGroupProps
> = ({
    creatorDownloadSource,
    samples,
    playbackMeta,
    isDisabled,
}: CreatorHeaderActionsGroupProps) => {
    const { state, dispatch, openShareModal } = useCreatorContext();
    const purchaseController = usePurchaseControllerContext();
    const { startPurchase } = purchaseController;
    const { isCreatorOpen, isSessionProcessing, samplesInSession } = state;

    const placeholder = getCollectionPlaceholder(playbackMeta);

    const {
        handleDownloadFullTrackClick,
        handleDownloadSamplesClick,
        handleAddSamplesToCollectionClick,
    } = useClickHandlers({
        startPurchase,
        creatorDownloadSource,
        dispatch,
        purchaseController,
    });

    return (
        <Group
            display="flex"
            alignItems="center"
            gutter="xs"
            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
        >
            <Group.Item>
                {samplesInSession.size >= 1 && (
                    <DropdownMenu>
                        <DropdownMenu.Trigger>
                            <Button
                                data-component="DownloadButton"
                                icon={DownloadOutlined}
                                variant="subtle"
                                isRounded
                                size="sm"
                                isDisabled={isSessionProcessing}
                                aria-label={t`Download`}
                            >
                                <Text size="sm">
                                    <Trans>Download</Trans>
                                </Text>
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.List>
                            <DropdownMenu.Item
                                onClick={handleDownloadFullTrackClick}
                            >
                                <Styled.DownloadMenuItemContainer>
                                    <Group direction={'vertical'}>
                                        <Trans>Full track</Trans>
                                        <Text contrast="low" size="sm">
                                            <Trans>Download your mixdown</Trans>
                                        </Text>
                                    </Group>
                                    <DownloadOutlined />
                                </Styled.DownloadMenuItemContainer>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                                onClick={handleDownloadSamplesClick}
                            >
                                <Styled.DownloadMenuItemContainer>
                                    <Group direction={'vertical'}>
                                        {SamplesBrand}
                                        <Text contrast="low" size="sm">
                                            <Trans>
                                                Download your processed samples
                                            </Trans>
                                        </Text>
                                    </Group>
                                    <DownloadOutlined />
                                </Styled.DownloadMenuItemContainer>
                            </DropdownMenu.Item>
                        </DropdownMenu.List>
                    </DropdownMenu>
                )}
            </Group.Item>
            <Group.Item>
                <ShareCreator
                    variant="subtle"
                    isRounded
                    size="sm"
                    label={t`Share your track`}
                    onClick={openShareModal}
                    isDisabled={isDisabled}
                />
            </Group.Item>
            <Group.Item>
                <AddSampleToCollectionButton
                    onClick={handleAddSamplesToCollectionClick}
                    samples={samples}
                    source={{ interaction: 'Creator' }}
                    popoverPlacement={isCreatorOpen ? 'left-start' : 'left-end'}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    size="sm"
                    variant="subtle"
                    isRounded
                />
            </Group.Item>
            <Group.Item>
                <RemoveFromCreatorButton
                    variant="subtle"
                    isRounded
                    size="sm"
                    isDisabled={isDisabled}
                />
            </Group.Item>
        </Group>
    );
};
